import React from 'react';
import styled from 'styled-components';
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import geography from "./features.json";
import coordinates from "./coordinates.json";
import {Country} from "types/analytics";
import { getRandomColor } from 'components/Analytics/utils';

interface MapProps {
  countries: Country[];
}

export const Map: React.FC<MapProps> = props => {
  const { countries } = props;
  const [tooltip, setTooltip] = React.useState<{ x: number; y: number; content: string } | null>(null);

  const handleEnter = (country: Country | undefined, evt: React.MouseEvent) => {
    if (!country) return;
    setTooltip({
      x: evt.clientX,
      y: evt.clientY,
      content: country.name,
    });
  };

  const handleLeave = () => {
    setTooltip(null);
  };

  const handleMove = (evt: React.MouseEvent) => {
    if (tooltip) {
      setTooltip((prev) => prev && { ...prev, x: evt.clientX, y: evt.clientY });
    }
  };
    const memoizedCountries = React.useMemo(() => {
      if (!props.countries?.length) return [];
      return props.countries.map(country => ({
        ...country,
        color: getRandomColor(),
      }));
    }, [props.countries])
  

  return (
    <Container>
      <Wrapper>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 0,
            center: [0, 0],
            rotate: [0, 0, 0]
          }}
          width={1000}
          height={1000}
        >
          <Geographies geography={geography}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: "#DDD",
                      outline: "none"
                    },
                    hover: {
                      fill: "#DDD",
                      outline: "none"
                    },
                    pressed: {
                      fill: "#DDD",
                      outline: "none"
                    }
                  }}
                />
              ))
            }
          </Geographies>
          {memoizedCountries.map((country) => {
            const geometry = geography.objects.world.geometries.find((geometry: { iso2: string; }) => {
              return geometry.iso2 === country.iso2;
            });

            if (!geometry) return null;

            // @ts-ignore
            const rawCoordinate = coordinates[geometry.id];

            try {
              const coordinate = rawCoordinate[0][0];
              return (
                <Marker
                  key={country.name}
                  coordinates={coordinate}
                  onMouseEnter={(e) => handleEnter(country, e)} // 👈 Важно!
                  onMouseMove={handleMove} // 👈 обновляем позицию
                  onMouseLeave={handleLeave} // 👈 убираем тултип
                >
                  <circle r={5} fill={country.color} stroke={country.color} strokeWidth={2} />
                  {/*<text textAnchor="middle" y={-10} style={{ fontSize: 10 }}>*/}
                  {/*  {country.name}*/}
                  {/*</text>*/}
                </Marker>
              );
            } catch (e) {
              console.error(e);
              return null;
            }
          })}
        </ComposableMap>
      </Wrapper>
      {tooltip && (
        <Tooltip style={{ top: tooltip.y + 10, left: tooltip.x + 10 }}>
          <div>
            <div className="content">
              {countries.map((country) => {
                if (tooltip.content !== country.name) {
                  return null;
                }
                return (
                  <div key={country.iso2} className="row">
                    <div className="info">
                      <span className="dot" style={{background: country.color}}></span>
                      <span className="label">{country.name}</span>
                    </div>
                    <span className="value">{country.total}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Tooltip>
      )}
    </Container>
  );
};

export const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: inline-block;
    vertical-align: middle;
  }

  path {
    fill: #EFEFEF;
  }
`;

const Wrapper = styled.div`
  width: 80%;
  height: 550px;
  overflow: hidden;
`;

const Tooltip = styled.div`
  position: fixed;
  width: 210px;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  transform: translate(10px, -100%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px 3px #BCBCBFAD;
  white-space: nowrap;

  .title {
    width: 100%;
    font-family: SF Pro Display;
    font-weight: 500;
    font-style: Medium;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #00000080;
    padding: 8px 12px;
    border-bottom: 1px solid #00000033;
  }

  .content {
    padding: 8px 12px;
    display: flex;
    gap: 15px;
    flex-direction: column;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    margin: 0 0;
  }

  .info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
  }

  .dot {
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 50%;
  }

  .label {
    font-family: SF Pro Display;
    font-weight: 400;
    font-style: Regular;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #000000;
  }

  .value {
    font-family: SF Pro Display;
    font-weight: 400;
    font-style: Regular;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #00000080;
  }
`;
